import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const BellIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14.8571 17C17.1265 17 18.2612 17 18.9678 16.2929C19.6745 15.5858 19.6736 14.4518 19.6717 12.1839L19.6658 10.184C19.6619 8.91828 19.5205 7.67788 18.7762 6.63288C18.1613 5.76917 17.2822 5.1277 16.2731 4.8009C15.8796 4.67346 15.7211 4.51192 15.6644 4.11894C15.5257 3.15736 14.7013 2.5 13.7297 2.5H10.2703C9.29873 2.5 8.47433 3.15736 8.33558 4.11894C8.27894 4.51192 8.12037 4.67346 7.72689 4.8009C6.71779 5.1277 5.83874 5.76917 5.22376 6.63288C4.47946 7.67788 4.33813 8.91828 4.33419 10.184L4.32831 12.1839C4.32642 14.4518 4.32548 15.5858 5.03216 16.2929C5.73885 17 6.87356 17 9.14298 17H14.8571Z" />
    <path d="M9.5 20C10.1012 20.6218 10.9806 21 12 21C13.0194 21 13.8988 20.6218 14.5 20" />
  </svg>
);

const formatDate = value => {
  if (!value) return "Ahora";
  try {
    return new Date(value).toLocaleString("es-CO");
  } catch (_error) {
    return value;
  }
};

export default function InternalNotificationsPopover() {
  const { user, socket } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [popupNotification, setPopupNotification] = useState(null);

  const unseenCount = useMemo(
    () => notifications.filter(item => !item.seen).length,
    [notifications]
  );

  const loadNotifications = async () => {
    try {
      const { data } = await api.get("/internal-notifications/me");
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (!user?.id) return undefined;
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    const unseen = notifications.filter(item => !item.seen);
    if (!unseen.length) {
      setPopupNotification(null);
      return;
    }

    setPopupNotification(current => {
      if (current && unseen.some(item => item.id === current.id)) {
        return current;
      }
      return unseen[0];
    });
  }, [notifications]);

  useEffect(() => {
    if (!socket) return undefined;

    const onInternalNotification = () => {
      loadNotifications();
    };

    socket.on("internal-notification", onInternalNotification);
    return () => socket.off("internal-notification", onInternalNotification);
  }, [socket]);

  const markAsRead = async notificationId => {
    try {
      await api.post(`/internal-notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(item =>
          item.id === notificationId
            ? { ...item, seen: true, seenAt: new Date().toISOString() }
            : item
        )
      );
      setPopupNotification(current => current?.id === notificationId ? null : current);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openDetails = notification => {
    setSelectedNotification(notification);
  };

  return (
    <>
      <IconButton onClick={handleOpen} style={{ color: "white" }}>
        <Badge badgeContent={unseenCount} color="secondary">
          <BellIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div style={{ width: 360, maxHeight: 420, overflowY: "auto", padding: 8 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700, padding: 8 }}>
            Notificaciones internas
          </Typography>
          <List dense>
            {notifications.map(item => (
              <ListItem
                key={item.id}
                button
                onClick={() => openDetails(item)}
                style={{
                  borderRadius: 10,
                  marginBottom: 6,
                  background: item.seen ? "transparent" : "rgba(25,118,210,0.08)"
                }}
              >
                <ListItemText
                  primary={item.title}
                  secondary={
                    <>
                      <span>{String(item.message || "").slice(0, 120)}</span>
                      <br />
                      <span>{formatDate(item.publishedAt || item.createdAt)}</span>
                    </>
                  }
                />
              </ListItem>
            ))}
            {!notifications.length && (
              <Typography variant="body2" color="textSecondary" style={{ padding: 12 }}>
                No hay notificaciones internas.
              </Typography>
            )}
          </List>
        </div>
      </Popover>

      <Dialog
        open={Boolean(selectedNotification)}
        onClose={() => setSelectedNotification(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedNotification?.title || "Notificacion"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
            {selectedNotification?.message}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
            Enviada: {formatDate(selectedNotification?.publishedAt || selectedNotification?.createdAt)}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!selectedNotification?.seen && (
            <Button
              onClick={async () => {
                await markAsRead(selectedNotification.id);
                setSelectedNotification(prev => prev ? { ...prev, seen: true, seenAt: new Date().toISOString() } : prev);
              }}
              color="primary"
              variant="outlined"
            >
              Marcar como vista
            </Button>
          )}
          <Button onClick={() => setSelectedNotification(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(popupNotification)}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        onClose={() => {}}
      >
        <DialogTitle>{popupNotification?.title || "Notificacion interna"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" style={{ whiteSpace: "pre-line", marginBottom: 12 }}>
            {popupNotification?.message}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Recibida: {formatDate(popupNotification?.publishedAt || popupNotification?.createdAt)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => markAsRead(popupNotification.id)}
            color="primary"
            variant="contained"
          >
            Marcar como vista
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
